import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ExclamationCircleIcon,
  SearchIcon,
  FilterIcon,
  EyeIcon,
  XCircleIcon,
  CheckCircleIcon,
  ClockIcon,
  CashIcon,
  DocumentTextIcon,
} from "@heroicons/react/outline";
import { getPendingCancellationsThunk } from "../../../store/thunks/jewelleryPlan/cancellationThunks";
import {
  selectPendingCancellations,
  selectPendingCancellationsPagination,
  selectPendingLoading,
  selectPendingError,
} from "../../../store/slices/jewelleryPlan/cancellationSlice";

const PendingCancellations = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const pendingCancellations = useSelector(selectPendingCancellations);
  const pagination = useSelector(selectPendingCancellationsPagination);
  const loading = useSelector(selectPendingLoading);
  const error = useSelector(selectPendingError);
  const { primaryBranch } = useSelector((state) => state.branch);

  const [filters, setFilters] = useState({
    branchId: primaryBranch?._id || "",
    branchCode: primaryBranch?.code || "",
    settlementType: "", // NEW: Filter by REFUND or COLLECTION
    page: 1,
    limit: 20,
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    dispatch(getPendingCancellationsThunk(filters));
  }, [dispatch, filters, primaryBranch]);

  const formatAmount = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
    }).format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getSettlementTypeBadge = (type) => {
    const config = {
      REFUND: {
        bg: "bg-blue-100",
        text: "text-blue-800",
        icon: CashIcon,
        label: "Refund Pending",
      },
      COLLECTION: {
        bg: "bg-orange-100",
        text: "text-orange-800",
        icon: DocumentTextIcon,
        label: "Collection Pending",
      },
    };

    const typeConfig = config[type] || config.REFUND;
    const Icon = typeConfig.icon;

    return (
      <span
        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${typeConfig.bg} ${typeConfig.text}`}
      >
        <Icon className="h-3.5 w-3.5 mr-1" />
        {typeConfig.label}
      </span>
    );
  };

  const getCancellationCategoryBadge = (category) => {
    const categoryConfig = {
      CUSTOMER_REQUEST: {
        bg: "bg-gray-100",
        text: "text-gray-800",
        label: "Customer Request",
      },
      FINANCIAL_HARDSHIP: {
        bg: "bg-yellow-100",
        text: "text-yellow-800",
        label: "Financial Hardship",
      },
      DEATH: {
        bg: "bg-purple-100",
        text: "text-purple-800",
        label: "Death",
      },
      FRAUD: {
        bg: "bg-red-100",
        text: "text-red-800",
        label: "Fraud",
      },
      ADMIN_ACTION: {
        bg: "bg-indigo-100",
        text: "text-indigo-800",
        label: "Admin Action",
      },
    };

    const config = categoryConfig[category] || categoryConfig.CUSTOMER_REQUEST;

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}
      >
        {config.label}
      </span>
    );
  };

  const getPaymentMethodBadge = (method) => {
    const methodConfig = {
      CASH: { bg: "bg-green-100", text: "text-green-800", label: "Cash" },
      BANK_TRANSFER: {
        bg: "bg-blue-100",
        text: "text-blue-800",
        label: "Bank Transfer",
      },
      UPI: { bg: "bg-purple-100", text: "text-purple-800", label: "UPI" },
      CHEQUE: { bg: "bg-yellow-100", text: "text-yellow-800", label: "Cheque" },
      ADJUSTMENT: {
        bg: "bg-gray-100",
        text: "text-gray-800",
        label: "Adjustment",
      },
    };

    const config = methodConfig[method] || methodConfig.CASH;

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs ${config.bg} ${config.text}`}
      >
        {config.label}
      </span>
    );
  };

  const handlePageChange = (newPage) => {
    setFilters({ ...filters, page: newPage });
  };

  const handleSettlementTypeFilter = (type) => {
    setFilters({ ...filters, settlementType: type, page: 1 });
  };

  const handleProcessRefund = (plan) => {
    // Navigate to refund processing page
    navigate(`/jewellery/cancellations/refunds/${plan.settlementReference}`);
  };

  const handleProcessCollection = (plan) => {
    // Navigate to collection processing page
    navigate(`/jewellery/collections/${plan.settlementReference}`);
  };

  const handleViewPlan = (planId) => {
    navigate(`/jewellery/${planId}`);
  };

  const filteredCancellations = pendingCancellations.filter((plan) => {
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (
      plan.planNumber?.toLowerCase().includes(search) ||
      plan.customer?.name?.toLowerCase().includes(search) ||
      plan.customer?.phoneNumber?.includes(search) ||
      plan.customer?.email?.toLowerCase().includes(search)
    );
  });

  // Calculate statistics
  const stats = {
    total: filteredCancellations.length,
    refunds: filteredCancellations.filter((p) => p.settlementType === "REFUND")
      .length,
    collections: filteredCancellations.filter(
      (p) => p.settlementType === "COLLECTION"
    ).length,
    totalRefundAmount: filteredCancellations
      .filter((p) => p.settlementType === "REFUND")
      .reduce((sum, p) => sum + (p.settlementAmount || 0), 0),
    totalCollectionAmount: filteredCancellations
      .filter((p) => p.settlementType === "COLLECTION")
      .reduce((sum, p) => sum + (p.settlementAmount || 0), 0),
  };

  if (loading && pendingCancellations.length === 0) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-text">
          Pending Settlements - Cancelled Plans
        </h1>
        <p className="mt-1 text-sm text-neutral-dark">
          Cancelled plans with pending refunds or collections requiring action
        </p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-background rounded-lg shadow p-4 border-l-4 border-gray-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-neutral-dark">Total Pending</p>
              <p className="text-2xl font-bold text-text">{stats.total}</p>
            </div>
            <ClockIcon className="h-10 w-10 text-gray-400" />
          </div>
        </div>
        <div className="bg-blue-50 rounded-lg shadow p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-700 font-medium">
                Refunds Pending
              </p>
              <p className="text-2xl font-bold text-blue-900">
                {stats.refunds}
              </p>
              <p className="text-xs text-blue-600 mt-1">
                {formatAmount(stats.totalRefundAmount)}
              </p>
            </div>
            <CashIcon className="h-10 w-10 text-blue-400" />
          </div>
        </div>
        <div className="bg-orange-50 rounded-lg shadow p-4 border-l-4 border-orange-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-orange-700 font-medium">
                Collections Pending
              </p>
              <p className="text-2xl font-bold text-orange-900">
                {stats.collections}
              </p>
              <p className="text-xs text-orange-600 mt-1">
                {formatAmount(stats.totalCollectionAmount)}
              </p>
            </div>
            <DocumentTextIcon className="h-10 w-10 text-orange-400" />
          </div>
        </div>
        <div className="bg-green-50 rounded-lg shadow p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-green-700 font-medium">
                Average Days Pending
              </p>
              <p className="text-2xl font-bold text-green-900">
                {filteredCancellations.length > 0
                  ? Math.round(
                      filteredCancellations.reduce((sum, p) => {
                        const daysPending = Math.floor(
                          (new Date() - new Date(p.cancelledOn)) /
                            (1000 * 60 * 60 * 24)
                        );
                        return sum + daysPending;
                      }, 0) / filteredCancellations.length
                    )
                  : 0}
              </p>
              <p className="text-xs text-green-600 mt-1">days</p>
            </div>
            <ClockIcon className="h-10 w-10 text-green-400" />
          </div>
        </div>
      </div>

      {/* Filters & Search */}
      <div className="bg-background rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-neutral-dark" />
              <input
                type="text"
                placeholder="Search by plan number, customer name, phone, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-neutral-light rounded-lg focus:ring-primary focus:border-primary"
              />
            </div>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-4 py-2 border border-neutral-light rounded-lg hover:bg-neutral-light flex items-center"
          >
            <FilterIcon className="h-5 w-5 mr-2" />
            Filters
          </button>
        </div>

        {/* Filter Options */}
        {showFilters && (
          <div className="mt-4 pt-4 border-t border-neutral-light">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleSettlementTypeFilter("")}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filters.settlementType === ""
                    ? "bg-primary text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                All Types
              </button>
              <button
                onClick={() => handleSettlementTypeFilter("REFUND")}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filters.settlementType === "REFUND"
                    ? "bg-blue-500 text-white"
                    : "bg-blue-100 text-blue-700 hover:bg-blue-200"
                }`}
              >
                Refunds Only
              </button>
              <button
                onClick={() => handleSettlementTypeFilter("COLLECTION")}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  filters.settlementType === "COLLECTION"
                    ? "bg-orange-500 text-white"
                    : "bg-orange-100 text-orange-700 hover:bg-orange-200"
                }`}
              >
                Collections Only
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="mb-6 bg-danger-light border border-danger rounded-lg p-4">
          <div className="flex items-start">
            <XCircleIcon className="h-5 w-5 text-danger mt-0.5 mr-2" />
            <div>
              <p className="text-sm font-medium text-danger-dark">Error</p>
              <p className="text-sm text-danger-dark mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Plans List */}
      <div className="bg-background rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-neutral-light">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cancellation Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Settlement
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Wallet Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-background divide-y divide-neutral-light">
              {filteredCancellations.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-8 text-center">
                    <CheckCircleIcon className="h-12 w-12 text-green-400 mx-auto mb-2" />
                    <p className="text-neutral-dark font-medium">
                      No pending settlements found
                    </p>
                    <p className="text-sm text-neutral mt-1">
                      All cancelled plans have been processed
                    </p>
                  </td>
                </tr>
              ) : (
                filteredCancellations.map((plan) => {
                  const daysPending = Math.floor(
                    (new Date() - new Date(plan.cancelledOn)) /
                      (1000 * 60 * 60 * 24)
                  );
                  const isUrgent = daysPending > 7;

                  return (
                    <tr
                      key={plan.planId}
                      className={`hover:bg-gray-50 transition-colors ${
                        isUrgent ? "bg-red-50" : ""
                      }`}
                    >
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-semibold text-text">
                            {plan.planNumber}
                          </p>
                          <p className="text-xs text-neutral-dark mt-0.5">
                            {plan.branch?.name || "N/A"}
                          </p>
                          <p className="text-xs text-neutral mt-1">
                            Completed: {plan.monthsCompleted || 0} months
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-text">
                            {plan.customer?.name || "N/A"}
                          </p>
                          <p className="text-xs text-neutral-dark">
                            {plan.customer?.phoneNumber || "N/A"}
                          </p>
                          {plan.customer?.email && (
                            <p className="text-xs text-neutral truncate max-w-xs">
                              {plan.customer.email}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-1.5">
                          <div>
                            {getCancellationCategoryBadge(
                              plan.cancellationCategory
                            )}
                          </div>
                          <p className="text-xs text-neutral-dark">
                            <span className="font-medium">Cancelled:</span>{" "}
                            {formatDate(plan.cancelledOn)}
                          </p>
                          {plan.cancelledBy?.name && (
                            <p className="text-xs text-neutral">
                              By: {plan.cancelledBy.name}
                            </p>
                          )}
                          {isUrgent && (
                            <p className="text-xs text-red-600 font-medium">
                              ⚠️ {daysPending} days pending
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="space-y-2">
                          <div>
                            {getSettlementTypeBadge(plan.settlementType)}
                          </div>
                          <p className="text-sm font-bold text-text">
                            {formatAmount(plan.settlementAmount)}
                          </p>
                          <div>
                            {getPaymentMethodBadge(plan.settlementMethod)}
                          </div>
                          {plan.settlementReference && (
                            <p className="text-xs text-neutral">
                              Ref: {String(plan.settlementReference).slice(-8)}
                            </p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-text">
                            {formatAmount(plan.walletValue)}
                          </p>
                          <p className="text-xs text-neutral-dark">
                            {(plan.walletGrams || 0).toFixed(3)}g
                          </p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col space-y-2">
                          <button
                            onClick={() => handleViewPlan(plan.planId)}
                            className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 rounded-md text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 transition-colors"
                            title="View Plan Details"
                          >
                            <EyeIcon className="h-4 w-4 mr-1" />
                            View Plan
                          </button>
                          {plan.settlementType === "REFUND" && (
                            <button
                              onClick={() => handleProcessRefund(plan)}
                              className="inline-flex items-center justify-center px-3 py-1.5 bg-blue-600 rounded-md text-xs font-medium text-white hover:bg-blue-700 transition-colors"
                              title="Process Refund"
                            >
                              <CashIcon className="h-4 w-4 mr-1" />
                              Process Refund
                            </button>
                          )}
                          {plan.settlementType === "COLLECTION" && (
                            <button
                              onClick={() => handleProcessCollection(plan)}
                              className="inline-flex items-center justify-center px-3 py-1.5 bg-orange-600 rounded-md text-xs font-medium text-white hover:bg-orange-700 transition-colors"
                              title="Process Collection"
                            >
                              <DocumentTextIcon className="h-4 w-4 mr-1" />
                              Process Collection
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-6 py-4 border-t border-neutral-light bg-gray-50">
            <div className="flex items-center justify-between">
              <div className="text-sm text-neutral-dark">
                Showing {(pagination.page - 1) * filters.limit + 1} to{" "}
                {Math.min(pagination.page * filters.limit, pagination.total)} of{" "}
                {pagination.total} results
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  className="px-4 py-2 border border-neutral-light rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <div className="flex items-center px-3 py-2 text-sm text-neutral-dark">
                  Page {pagination.page} of {pagination.pages}
                </div>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.pages}
                  className="px-4 py-2 border border-neutral-light rounded-lg hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PendingCancellations;
